import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail, Instagram, Facebook, Clock, Sparkles, ShieldCheck } from "lucide-react";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { Monogram } from "../components/Monogram";
import { Motif } from "../components/Motifs";
import { ARTISTS } from "../data/artists";
import { CONTACT } from "../data/site";
import { MOOD_IMAGES } from "../data/gallery";

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const CHAPTERS = [
  {
    num: "01",
    title: "Hagyomány × jövő",
    text: "Szeptember 1-jén nyitottuk meg kapuinkat Budapesten. A stúdió letisztult, prémium európai környezetet képvisel: visszafogott elegancia, természetes anyagok és nyugodt légkör — egy tér, ahol a régi mesterek tisztelete találkozik a mai tetováláskultúrával.",
  },
  {
    num: "02",
    title: "Átlátható árak",
    text: "Nincs apróbetű, nincs meglepetés. A tetoválások tűbontási alapára 20 000 Ft, a végső ár pedig egyedi — a piercing árlista tételesen, nyilvánosan érhető el. Amit megbeszélünk, azt kapod — az árban is, a munkában is.",
  },
  {
    num: "03",
    title: "Nyolc művész, egy ház",
    text: "Realisztikus portréktól a fineline vonalakig, dark kompozícióktól a henna és jagua testdíszítésig — nyolc művész, nyolc kéz, egy közös stúdió a Podmaniczky utcában.",
  },
];

const initials = (name) => name.split(" ").map((w) => w[0]).join("");

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollToContact = () => {
    const el = document.getElementById("kapcsolat");
    if (el) window.__lenis?.scrollTo(el, { offset: -60 });
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-center overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={MOOD_IMAGES.hero} alt="Tetováló művész munka közben, sötét stúdiófények" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0A]/45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-[#0A0A0A]/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/50" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-4"
          >
            <Monogram size="lg" />
            <p className="eyebrow" data-testid="hero-eyebrow">
              Shuten Tattoo Studio — Budapest
            </p>
          </motion.div>

          <h1 className="font-display text-[15vw] font-medium leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-[7.5rem]" data-testid="hero-title">
            <HeroLine delay={0.35}>Gyere el</HeroLine>
            <HeroLine delay={0.5}>
              <span className="italic text-[#C04B58]">hozzánk.</span>
            </HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300"
          >
            Szeptember 1-jén nyitottunk Budapesten, a Podmaniczky utca 75-ben. Tetoválás, piercing, henna és jagua —
            letisztult, prémium környezetben, nyolc művésszel.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button onClick={scrollToContact} data-testid="hero-appointment-btn" className="btn-primary">
              Időpontkérés <ArrowRight size={16} />
            </button>
            <Link to="/muveszek" data-testid="hero-artists-btn" className="btn-ghost">
              Művészeink
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2 text-zinc-500">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">Görgess</span>
            <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="h-8 w-px bg-gradient-to-b from-[#9A3542] to-transparent" />
          </div>
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section className="relative mx-auto max-w-7xl overflow-hidden px-5 py-24 sm:px-8 sm:py-32" data-testid="manifesto-section">
        <Motif type="needle" className="pointer-events-none absolute -right-6 top-12 w-44 select-none text-[#C04B58] opacity-[0.06]" />
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-12 bg-[#9A3542]" />
            <p className="eyebrow">A stúdió</p>
          </div>
          <h2 className="font-display mt-4 max-w-2xl text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
            Tinta, tisztelet, <span className="italic text-[#C04B58]">precizitás.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.12}>
              <article className="card-dark group h-full p-8" data-testid={`chapter-${c.num}`}>
                <span className="font-mono2 text-sm tracking-[0.3em] text-[#9A3542]">{c.num} /</span>
                <h3 className="font-display mt-5 text-2xl font-semibold text-white transition-colors group-hover:text-[#C04B58]">
                  {c.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SHUTEN DOJI — A NÉV EREDETE */}
      <section className="relative overflow-hidden border-y border-[#202023] bg-[#0F0F11] py-24 sm:py-32" data-testid="shuten-doji-section">
        <Motif type="wave" className="pointer-events-none absolute -right-10 top-1/2 w-[420px] -translate-y-1/2 select-none text-[#C04B58] opacity-[0.05]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="h-px w-12 bg-[#9A3542]" />
              <p className="eyebrow">A név eredete</p>
            </div>
            <h2 className="font-display mt-4 text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
              Ki volt <span className="italic text-[#C04B58]">Shuten Doji?</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400" data-testid="shuten-doji-text">
              A stúdió neve a japán mondavilág egyik leghíresebb alakjából ered. A legenda szerint Shuten Doji az
              Óe-hegy démonkirálya volt — erős, karizmatikus és szabályokat nem ismerő —, akit végül Minamoto no
              Jorimicu győzött le egy hírhedt hadművelet során. A történet évszázadok óta a japán vizuális kultúra,
              a fatöblenyomatok és nem utolsósorban a tetoválásművészet egyik kedvelt alakja.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
              Számunkra a név hármas ígéret: erő, karakter és történet. Pontosan az, amit egy jó tetoválás is hordoz
              — és amit minden vendégünkkel együtt írunk újra, bőrön.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="card-dark relative p-10 sm:p-12" data-testid="shuten-doji-quote">
              <Motif type="flower" className="absolute right-8 top-8 w-16 text-[#9A3542] opacity-30" />
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">A név jelentése számunkra</p>
              <p className="font-display mt-5 text-3xl font-medium italic leading-snug text-white sm:text-4xl">
                „Erő, karakter, történet."
              </p>
              <p className="mt-5 text-sm leading-relaxed text-zinc-400">
                A démonkirály legendája a bátorság és a különlegesség szimbóluma — ugyanaz a merészség, amivel a
                vendégeink egy maradandó mintára vállalkoznak.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ARTISTS TEASER */}
      <section className="relative overflow-hidden border-y border-[#202023] bg-[#0F0F11] py-24 sm:py-32" data-testid="artists-teaser">
        <Motif type="flash" className="pointer-events-none absolute -left-8 bottom-10 w-36 select-none text-[#C04B58] opacity-[0.06]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-4">
                <span className="h-px w-12 bg-[#9A3542]" />
                <p className="eyebrow">Tetoválóink</p>
              </div>
              <h2 className="font-display mt-4 text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">
                Nyolc kéz, <span className="italic text-[#C04B58]">nyolc világ.</span>
              </h2>
            </div>
            <Link to="/muveszek" data-testid="teaser-all-artists-btn" className="btn-ghost">
              Összes művész <ArrowRight size={16} />
            </Link>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {ARTISTS.slice(0, 4).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link to="/muveszek" data-testid={`teaser-artist-${a.slug}`} className="card-dark group block overflow-hidden">
                  <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-[#0f0f0f]">
                    {a.photo ? (
                      <img
                        src={a.photo}
                        alt={`${a.name} portré`}
                        className={`absolute inset-0 h-full w-full object-cover ${a.photoPos || "object-top"} transition-transform duration-700 group-hover:scale-110`}
                      />
                    ) : (
                      <>
                        <img
                          src={MOOD_IMAGES.mist}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover opacity-15 transition-transform duration-700 group-hover:scale-110"
                        />
                        <span className="font-display relative text-5xl font-medium tracking-[0.15em] text-white/85 transition-colors duration-500 group-hover:text-[#C04B58]">
                          {initials(a.name)}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="font-display text-lg font-semibold text-white">{a.name}</p>
                    <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">{a.tags[0]}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="relative mx-auto max-w-7xl overflow-hidden px-5 py-24 sm:px-8 sm:py-32" data-testid="pricing-teaser">
        <Motif type="machine" className="pointer-events-none absolute -right-8 bottom-8 w-48 select-none text-[#C04B58] opacity-[0.06]" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="card-dark h-full p-10">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-[#9A3542]" />
                <p className="eyebrow">Tetoválás</p>
              </div>
              <p className="font-display mt-6 text-5xl font-semibold text-white sm:text-6xl" data-testid="teaser-price">
                20 000 Ft<span className="text-2xl text-zinc-500">-tól</span>
              </p>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-zinc-400">
                Tűbontási alapár — a tetoválások egyedi árazással készülnek, a végső ár a mintától, méretétől és
                helyétől függ.
              </p>
              <Link to="/arak" data-testid="teaser-pricing-btn" className="btn-primary mt-8">
                Részletes árak <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="card-dark h-full p-10">
              <div className="flex items-center gap-3">
                <Sparkles size={18} className="text-[#9A3542]" />
                <p className="eyebrow">Walk-in</p>
              </div>
              <h3 className="font-display mt-6 text-3xl font-semibold text-white">Besétálós napok, <span className="italic text-[#C04B58]">minden nap.</span></h3>
              <p className="mt-5 text-sm leading-relaxed text-zinc-400">
                Nem szeretnél időpontot foglalni? Gyere be nyitvatartási időn belül — minden nap dolgozik bent egy
                tetováló, aki fogadja a walk-in vendégeket.
              </p>
              <div className="mt-8 flex items-center gap-3 text-sm text-zinc-400">
                <ShieldCheck size={18} className="text-[#9A3542]" /> Steril környezet, egyszerhasználatos eszközök.
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-6">
          <Link
            to="/termekek"
            data-testid="teaser-products-btn"
            className="card-dark group flex items-center justify-between gap-5 p-7 sm:p-8"
          >
            <div className="flex items-center gap-5">
              <Motif type="flower" className="w-10 shrink-0 text-[#9A3542]" />
              <div>
                <p className="font-display text-xl font-semibold text-white transition-colors group-hover:text-[#C04B58] sm:text-2xl">
                  Utókezelő termékek & kortárs festmények
                </p>
                <p className="mt-1 text-sm text-zinc-400">Krémek, kenőcsök és művészi alkotások — megtekinthetők a szalonban.</p>
              </div>
            </div>
            <ArrowRight size={20} className="shrink-0 text-zinc-500 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#C04B58]" />
          </Link>
        </Reveal>
        <Reveal className="mt-5">
          <Link
            to="/arak#sminktetovalas"
            data-testid="teaser-pmu-btn"
            className="card-dark group flex items-center justify-between gap-5 p-7 sm:p-8"
          >
            <div className="flex items-center gap-5">
              <Motif type="needle" className="w-10 shrink-0 text-[#9A3542]" />
              <div>
                <p className="font-display text-xl font-semibold text-white transition-colors group-hover:text-[#C04B58] sm:text-2xl">
                  Sminktetoválás & lézeres eltávolítás
                </p>
                <p className="mt-1 text-sm text-zinc-400">
                  Szemöldök- és szemhéjtetoválás, eltávolítás 25 000 – 40 000 Ft / alkalom — stílusok, gyógyulás, utóápolás.
                </p>
              </div>
            </div>
            <ArrowRight size={20} className="shrink-0 text-zinc-500 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#C04B58]" />
          </Link>
        </Reveal>
      </section>

      {/* CONTACT / IDŐPONT */}
      <section id="kapcsolat" className="relative overflow-hidden border-t border-[#202023] bg-[#0F0F11] py-24 sm:py-32" data-testid="home-contact-section">
        <Motif type="ring" className="pointer-events-none absolute -left-10 top-16 w-40 select-none text-[#C04B58] opacity-[0.06]" />
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="h-px w-12 bg-[#9A3542]" />
              <p className="eyebrow">Időpont & kapcsolat</p>
            </div>
            <h2 className="font-display mt-4 text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
              Beszéljük meg <span className="italic text-[#C04B58]">személyesen.</span>
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400">
              Az időpontkérés és egyeztetés manuálisan történik — írj nekünk Instagramon, Facebookon, e-mailben, vagy
              hívj minket telefonon.
            </p>
            <ul className="mt-10 space-y-5">
              <li className="flex items-center gap-4">
                <span className="rounded-full border border-white/10 p-3 text-[#9A3542]"><MapPin size={18} /></span>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">Cím</p>
                  <p className="text-white" data-testid="home-contact-address">{CONTACT.address}</p>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="rounded-full border border-white/10 p-3 text-[#9A3542]"><Phone size={18} /></span>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">Telefon</p>
                  <a href={CONTACT.phoneHref} data-testid="home-contact-phone" className="text-white transition-colors hover:text-[#C04B58]">{CONTACT.phone}</a>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="rounded-full border border-white/10 p-3 text-[#9A3542]"><Mail size={18} /></span>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">E-mail</p>
                  <a href={`mailto:${CONTACT.email}`} data-testid="home-contact-email" className="text-white transition-colors hover:text-[#C04B58]">{CONTACT.email}</a>
                </div>
              </li>
            </ul>
            <div className="mt-10 flex gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="home-instagram-btn" className="btn-primary">
                <Instagram size={16} /> Instagram
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" data-testid="home-facebook-btn" className="btn-ghost">
                <Facebook size={16} /> Facebook
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="card-dark overflow-hidden">
              <iframe
                title="Shuten Tattoo Studio térkép"
                data-testid="home-map"
                src={CONTACT.mapsEmbed}
                className="h-full min-h-[420px] w-full grayscale invert-[0.92] contrast-[0.9]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
